// Device dimensions - Specific device models for clinical validation
export const DEVICE_DIMENSIONS = {
  // Mobile Devices
  'iphone-se': { width: 375, height: 667, name: 'iPhone SE/8 (375x667)', category: 'phone' },
  'iphone-12': { width: 390, height: 844, name: 'iPhone 12/13 (390x844)', category: 'phone' },
  'iphone-14-pro': { width: 393, height: 852, name: 'iPhone 14 Pro (393x852)', category: 'phone' },
  'samsung-s21': { width: 360, height: 800, name: 'Samsung Galaxy S21 (360x800)', category: 'phone' },
  'pixel-5': { width: 393, height: 851, name: 'Google Pixel 5 (393x851)', category: 'phone' },
  
  // Tablets
  'ipad-10': { width: 810, height: 1080, name: 'iPad 10.2" (810x1080)', category: 'tablet' },
  'ipad-air': { width: 820, height: 1180, name: 'iPad Air (820x1180)', category: 'tablet' },
  'ipad-pro-11': { width: 834, height: 1194, name: 'iPad Pro 11" (834x1194)', category: 'tablet' },
  'ipad-pro-13': { width: 1024, height: 1366, name: 'iPad Pro 12.9" (1024x1366)', category: 'tablet' },
  'galaxy-tab-s8': { width: 800, height: 1280, name: 'Galaxy Tab S8 (800x1280)', category: 'tablet' },
  'surface-go': { width: 800, height: 1280, name: 'Surface Go (800x1280)', category: 'tablet' },
  
  // Desktop/Laptop
  'laptop-13': { width: 1280, height: 800, name: 'MacBook Air 13" (1280x800)', category: 'desktop' },
  'laptop-14': { width: 1440, height: 900, name: 'MacBook Pro 14" (1440x900)', category: 'desktop' },
  'laptop-15': { width: 1366, height: 768, name: 'Common Laptop 15" (1366x768)', category: 'desktop' },
  'desktop-hd': { width: 1920, height: 1080, name: 'Desktop HD (1920x1080)', category: 'desktop' },
  'desktop-standard': { width: 1200, height: 800, name: 'Standard Desktop (1200x800)', category: 'desktop' },
  
  // Clinical Device Presets
  'clinical-tablet': { width: 768, height: 1024, name: 'Clinical Tablet Standard (768x1024)', category: 'tablet' },
  'clinical-laptop': { width: 1280, height: 900, name: 'Clinical Laptop Standard (1280x900)', category: 'desktop' },

  // Responsive (fluid) canvas — not a fixed device size. The canvas fills the
  // full width of whatever screen it is displayed on (builder canvas, preview,
  // and participant device). The width/height here are the *nominal* design-space
  // dimensions used for the builder coordinate system and element-scaling math;
  // the actual rendered width is always 100% of the available container — see
  // CanvasArea (builder) and ScreenRenderer (preview / survey-taking). The
  // `responsive: true` flag is the canonical discriminator across the app.
  'responsive': { width: 1200, height: 800, name: 'Responsive (Full width)', category: 'responsive', responsive: true }
};

// Response option presets for quick fill
export const RESPONSE_OPTION_PRESETS = {
  'phq9': [
    { order: 1, label: 'Not at all', value: 0 },
    { order: 2, label: 'Several days', value: 1 },
    { order: 3, label: 'More than half the days', value: 2 },
    { order: 4, label: 'Nearly every day', value: 3 }
  ],
  'gad7': [
    { order: 1, label: 'Not at all', value: 0 },
    { order: 2, label: 'Several days', value: 1 },
    { order: 3, label: 'More than half the days', value: 2 },
    { order: 4, label: 'Nearly every day', value: 3 }
  ],
  'nrs': Array.from({ length: 11 }, (_, i) => ({ 
    order: i + 1, 
    label: i.toString(), 
    value: i 
  })),
  'severity': [
    { order: 1, label: 'None', value: 0 },
    { order: 2, label: 'Mild', value: 1 },
    { order: 3, label: 'Moderate', value: 2 },
    { order: 4, label: 'Severe', value: 3 }
  ],
  'likert5': [
    { order: 1, label: 'Strongly Disagree', value: 0 },
    { order: 2, label: 'Disagree', value: 1 },
    { order: 3, label: 'Neutral', value: 2 },
    { order: 4, label: 'Agree', value: 3 },
    { order: 5, label: 'Strongly Agree', value: 4 }
  ],
  'yesno': [
    { order: 1, label: 'Yes', value: 1 },
    { order: 2, label: 'No', value: 0 }
  ]
};

// Preset labels for dropdown display
export const PRESET_LABELS = {
  'phq9': 'PHQ-9 Scale (0-3)',
  'gad7': 'GAD-7 Scale (0-3)',
  'nrs': 'NRS 0-10',
  'severity': 'Severity (None to Severe)',
  'likert5': 'Likert 5-Point',
  'yesno': 'Yes/No'
};

// Default element sizes for drag preview
export const DEFAULT_ELEMENT_SIZES = {
  question: { width: 500, height: 200 },
  text: { width: 400, height: 60 },
  button: { width: 120, height: 40 },
  image: { width: 400, height: 200 },
  divider: { width: 400, height: 2 },
  'text-input': { width: 300, height: 40 },
  'number-input': { width: 150, height: 40 },
  'date-picker': { width: 200, height: 40 },
  checkbox: { width: 300, height: 100 },
  scale: { width: 400, height: 80 }
};

// Zoom presets
export const ZOOM_PRESETS = [
  { value: 0.25, label: '25%' },
  { value: 0.5, label: '50%' },
  { value: 0.75, label: '75%' },
  { value: 1, label: '100%' },
  { value: 1.25, label: '125%' },
  { value: 1.5, label: '150%' },
  { value: 2, label: '200%' }
];

// Default sidebar width
export const DEFAULT_SIDEBAR_WIDTH = 384;

// Grid size options
export const GRID_SIZE_OPTIONS = [5, 10, 15, 20, 25];

// Question type display names — canonical source of truth for all AB components
export const QUESTION_TYPE_DISPLAY = {
  likert: 'Ordinal',
  ordinal: 'Ordinal',
  rating: 'Rating',
  text: 'Text',
  free_text: 'Text',
  yes_no: 'Yes/No',
  nominal: 'Nominal',
  multiple_choice: 'Multiple Choice',
  checkbox: 'Checkbox',
  dropdown: 'Dropdown',
  date: 'Date',
  boolean: 'Yes/No',
  vas: 'VAS',
  nrs: 'NRS',
  numeric: 'Numeric',
  body_map: 'Body Map',
};

export const displayQuestionType = (type) => QUESTION_TYPE_DISPLAY[type] || type;

// Font families offered for form-wide and per-element formatting. Mirrors the
// per-element list in PropertiesPanel so both pickers stay in sync.
export const FONT_FAMILY_OPTIONS = [
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: '"Times New Roman", serif', label: 'Times New Roman' },
  { value: 'Helvetica, Arial, sans-serif', label: 'Helvetica' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Verdana, sans-serif', label: 'Verdana' },
  { value: '"Courier New", monospace', label: 'Courier New' },
  { value: 'system-ui, sans-serif', label: 'System UI' }
];

// Line-spacing presets surfaced as quick-pick buttons (FECOA-732). The numeric
// input still accepts any value in [LINE_SPACING_MIN, LINE_SPACING_MAX].
export const LINE_SPACING_PRESETS = [
  { value: 1.0, label: 'Single' },
  { value: 1.15, label: '1.15' },
  { value: 1.5, label: '1.5' },
  { value: 2.0, label: 'Double' }
];
export const LINE_SPACING_MIN = 0.8;
export const LINE_SPACING_MAX = 3.0;
export const LINE_SPACING_STEP = 0.05;

// Form-wide formatting defaults. Must match DEFAULT_GLOBAL_FORMATTING on the
// backend (assessment_builder/models_template.py). Used as the fallback when an
// assessment has no global_formatting yet and as the "Reset to Publisher Defaults" target.
export const DEFAULT_GLOBAL_FORMATTING = {
  fontFamily: 'Arial, sans-serif',
  baseFontSize: 16,
  lineSpacing: 1.5
};
